import {Form, useLocation, useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useReducer, useRef, useState} from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import {TextField, MenuItem, Button, Stack, IconButton, ImageList, ImageListItem, ImageListItemBar, ListSubheader, CircularProgress, Autocomplete, Checkbox, FormControlLabel, Tooltip, FormControl, InputLabel, OutlinedInput, InputAdornment} from '@mui/material';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getAssetById, deleteAssetById, putAssetById} from "../../API/assetAPI";

import useAPI from "../../../services/hooks/useAPI";
import {ConfirmationDialog} from "../../dialogs/confirmation-dialog";
import {HashLink as Link} from "react-router-hash-link";
import {Helmet} from "react-helmet";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { itemsForDownloadState } from "../../../atoms/itemsForDownload";
import axios from "axios";
import { User } from "oidc-client";
import { CacheService } from "../../../common/services/cache.service";
import environment from "../../../environment/environment";
import APIService from "../../../services/api.service";
import { userState } from "../../../atoms/user";
import { ViewDownloadsDialog } from "../../dialogs/View-Downloads-Dialog";
import APIOptions from "../../../models/apiOptions";
import { Record as Asset } from "../../../models/asset";
import { adminState } from "../../../atoms/isAdmin";
import { Cart } from "../../../models/assetResponse";
import { cartState } from "../../../atoms/cart";
import { tokenState } from "../../../atoms/token";
import { UploadDialog } from "../../dialogs/UploadDialog";
import { Parks } from "../../../models/ticNum";
import { parksState } from "../../../atoms/parks";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs'; 
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { initialRecordState } from "../../../models/Record";

const WhiteOutlinedTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff'
  },
});

const WhiteOutlinedDateField = styled(DatePicker)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff'
  },
});

const Record = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { Id: paramId } = useParams<{Id:string}>();
  const pageId = paramId ?? null;
  const getAssetAPI = useAPI(getAssetById);
  const putAssetAPI = useAPI(putAssetById);
  const deleteAssetAPI = useAPI(deleteAssetById);

  // form & UI state
  const [form, setForm] = useReducer((form, update) => ({...form, ...update}), {});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [parks, setParks] = useState<any[]>([]);
  const [securityTypes, setSecurityTypes] = useState<any[]>([]);
  const [uniformTitle, setUniformTitle] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [rights, setRights] = useState<string[]>([]);
  const [condition, setCondition] = useState<string[]>([]);
  const [size, setSize] = useState<string[]>([]);
  const [storage, setStorage] = useState<string[]>([]);
  const [mediaType, setMediaType] = useState<string[]>([]);
  const [retentionCategory, setRetentionCategory] = useState<string[]>([]);
  const [retentionDisposition, setRetentionDisposition] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [locationLists, setLocationLists] = useState([]);
  const [children, setChildren] = useState([]);
  const [addRemoveToggle, setAddRemoveToggle] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pageCount, setPageCount] = useState(0);

  const [token] = useRecoilState(tokenState);
  const [userInfo] = useRecoilState(userState);
  const [isAdmin, setIsAdmin] = useRecoilState(adminState);
  const [cartItems, SetCartState] = useRecoilState(cartState);

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'Title', width: 200 },
    { field: 'pdfName', headerName: 'File Name', width: 200 },
  ];

  const getParks = (): Promise<void> =>
    new APIService<any[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticpark&format=JSON`, token)
      .getAnyAsync().then((res:any) => setParks(res));

  const getSecurityTypes = (): Promise<void> =>
    new APIService<any[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticdoctype&format=JSON`, token)
      .getAnyAsync().then((res:any) => setSecurityTypes(res.map((i:any) => ({ label: i.description, value: i.label }))));

  const getUniformTitle = (): Promise<void> =>
    new APIService<any[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticuniformtitle&format=JSON`, token)
      .getAnyAsync().then((res:any) => setUniformTitle(res));

  const getSubjects = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticsubject&format=JSON`, token)
      .getAnyAsync().then((res:any) => setSubjects(res.filter(x => x)));

  const getRights = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticrights&format=JSON`, token)
      .getAnyAsync().then((res: string[]) => setRights(res.map((s) => s.trim())));

  const getCondition = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticcondition&format=JSON`, token)
      .getAnyAsync().then((res:any) => setCondition(res));

  const getSize = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticsize&format=JSON`, token)
      .getAnyAsync().then((res:any) => setSize(res));

  const getStorage = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticstorage&format=JSON`, token)
      .getAnyAsync().then((res:any) => setStorage(res));

  const getMediaType = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticmedia&format=JSON`, token)
      .getAnyAsync().then((res:any) => setMediaType(res));

  const getRetentionCategory = (): Promise<void> =>
    new APIService<string[]>(axios, new CacheService(), `${environment.npgAPI}list?id=eticretentioncategory&format=JSON`, token)
      .getAnyAsync().then((res:any) => setRetentionCategory(res));

  const init = async function init() 
    {
      await Promise.all([
        getParks(),
        getSecurityTypes(),
        getUniformTitle(),
        getSubjects(),
        getRights(),
        getMediaType(),
        getCondition(),
        getSize(),
        getStorage(),
        getRetentionCategory()
      ]);
    };


  useEffect(() => {
  //Get All values    
    init();
  }, [])

  /* GETTING RECORD DATA */
  useEffect(() => {
    init();

    // 1) Reset everything
    setForm(initialRecordState);
    setChildren([]);
    setPageCount(0);
    setLoading(true);
    setIsEditing(false);   

    // 2) Then load for this ID or blank-new
    if (pageId) 
    {
      getTicParentChildren(pageId);
    } 
    else if (state?.form) 
    {        
      getLocations(state?.form?.parkName);

      console.log('getting form',state.form);

      if(state?.form?.parentStub?.children.length > 0)
        setChildren(state?.form?.parentStub?.children);
      
      if(state.form?.parkName){
        state.form.ticParkName = state.form?.parkName;
        state.form.ticParkAlpha = state.form?.parkAlpha;
        state.form.ticParkCode = state.form?.parkCode;
        state.form.ticParkId = state.form?.parkId;
        state.form.ticParkPreviousName = state.form?.parkPreviousName;
        state.form.ticParkRegionName = state.form?.parkRegionName;
        state.form.ticComment = state.form?.comments;
        state.form.ticDocType = state.form?.docType;
      }

      setForm(state.form);
      //immediately load the matching disposition list
      if (state.form.ticRetentionCategory) {
        getRetentionDisposition(state.form.ticRetentionCategory);
      }
      
      setLoading(false);
    } 
    else 
    {
      setLoading(false);
      setIsEditing(true);
    }

    getCart();
  }, [pageId]);

  useEffect(() => { 
    let exists = cartItems.filter((row) => form?.ticParentDocId === row?.parentDocId).length ? true : false;
    console.log('exists', exists);
    setAddRemoveToggle(!exists);
  }, [cartItems, form]);

  useEffect(() => {
    let roles: string[] = userInfo?.roles ?? [];
    setIsAdmin(roles.includes('etic_admin'));
    console.log('isadmin', isAdmin)
    console.log('isadmin', userInfo)

  }, [userInfo]);

  const getTicParentChildren = (ticParentDocId) => { 
    console.log('get children', ticParentDocId);

    const url = `${environment.eticApiUrl}getparent/${ticParentDocId}`;
    const apiOptions: APIOptions = new APIOptions(url, Asset);
    const T = apiOptions.T;

    const apiService = new APIService<typeof T>(axios, new CacheService, apiOptions.url, token);
    apiService.getAnyAsync().then((res: any) => { 
      setForm(res);

      //immediately load the matching disposition list
      if (res.ticRetentionCategory) {
        getRetentionDisposition(res.ticRetentionCategory);
      }
      console.log('children', res.children);

      if(res?.children?.length > 0)  {
        setChildren(res.children);

        let count = 0;
        res.children.forEach(x => {
          if(x?.pdfPageCount)
            count += x.pdfPageCount;
        });
        setPageCount(count);

      }else{
        setChildren([]);
      }
      setLoading(false);
    });
  }

  /* FORM FIELD SETUP */
  const securityVerifications =  [ {
      value: 'Needs Verification',
      label: 'Needs Verification',
    },
    {
      value: 'Verified',
      label: 'Verified',
    }
  ];

  const infoTypes= [ {
      value: 'Boundary Survey',
      label: 'Boundary Survey',
    },
    {
      value: 'E-TIC Drawing',
      label: 'Drawing',
    },
    {
      value: 'C-TIC Electronic',
      label: 'Electronic',
    },
    {
      value: 'D-TIC Document',
      label: 'Document',
    }, {
      value: 'F-TIC Aerial',
      label: 'Aerial',
    }, {
      value: 'M-TIC Map',
      label: 'Map',
    }, {
      value: 'V-TIC Visual',
      label: 'Visual',
    }, {
      value: 'W-TIC Wayside Exhibit',
      label: 'Wayside Exhibit'
    }
  ];

  const onlyNumbers = (e) => { e.target.value = e.target.value.replace(/[^0-9]/g, '') };

  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;

  const getRetentionDisposition = (category: string) => {
    let url = `${environment.npgAPI}list?id=eticretentiondisposition&format=JSON`;
    const apiService = new APIService<any>(axios, new CacheService, url, token);
    apiService.getAnyAsync().then((res:any) => {
      console.log('retentiondisposition', res);
      // get first two characters from category
      let cat = category.substring(0, 2);
      // check if first two characters are in the disposition
      let dispositions = res.filter(x => x.substring(0, 2) === cat);
      setRetentionDisposition(dispositions);
    });
  }

  const getLocations = (parkName?: string) => {
    console.log('husky loca', parkName);

    let parkQuery = '';
    let parkId = parks.find(x => x.name === parkName)?.id;
    console.log('husky loca id', parkId);

    if(parkName && parkId) {
      parkQuery = '&filter=parkid:' + parkId;
      let url = `${environment.npgAPI}list?id=eticlocation&format=JSON${parkQuery}`;
      const apiService = new APIService<any>(axios, new CacheService, url, token);

      apiService.getAnyAsync().then((res:any) => {
        console.log('loca', res);

        setLocations(res);
      });
    } else {
      let url = `${environment.npgAPI}list?id=eticlocation&format=JSON`;
      const apiService = new APIService<any>(axios, new CacheService, url, token);

      apiService.getAnyAsync().then((res:any) => {
        console.log('loca l', res);

        setLocationLists(res);
      });
    }
  }

  /* SAVE RECORD DATA */
  const handleSaveFileEdit = (children) => { 
      setShowUploadDialog(false);
      setLoading(true);

      const url = `${environment.eticApiUrl}getparent/${form?.ticParentDocId}`;
      const apiOptions: APIOptions = new APIOptions(url, Asset);
      const T = apiOptions.T;
      const apiService = new APIService<typeof T>(axios, new CacheService, apiOptions.url, token);
      apiService.getAnyAsync().then((res: any) => { 
        const url = `${environment.eticApiUrl}updateRecord/${form?.ticParentDocId}`;
        console.log('get res', res);
        let rec = res;
        
        //Update the returned children
        rec.children = children;

        console.log('posting rec', rec);
        console.log('children', children);
        axios.post(url, rec, { headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }, withCredentials: true
        }).then(res => {
          console.log(res);
          setLoading(false);
          getTicParentChildren(form?.ticParentDocId);
        }).catch(err => { 
          setLoading(false);
          getTicParentChildren(form?.ticParentDocId);
        });
      });
    }

    const handleSubmit = (event) => {
        event.preventDefault();
    }

    const onSaveClick = () => {

      console.log('onsaveclick', pageId, form);
      setIsSaving(true);

      if(form?.ticParentDocId) {
        //Copy and add on the current children
        let formCopy = form;
        formCopy.children = children;

        putAssetAPI.request(form.id, formCopy);
        setIsSaving(false);

      } else {
        console.log('create new', form);
        const url = `${environment.eticApiUrl}CreateRecord`;
        const apiService = new APIService<any>(axios, new CacheService, url, token);

        //Create a new form object to handle conversion if needed
        let formObject = {};
        //Copy, removing the parentStub and add on the current children
        let { parentStub, ...rest} = form;
        //const formCopy = { ...rest, children };

        //Decide if we Convert a UserUpload or Create a new record
        if(state?.form?.id){
          formObject = {
            id: state.form.id,
            ...rest,
            children
            //parentStub: { formCopy }
          };
        } else {
          formObject = { ...rest, children };
        }

        apiService.add(formObject).then((res:any) => {
          console.log('complete', res);

          //set url param to id
          navigate(`/record/${res.ticParentDocId}`);

          setIsSaving(false);
        });
      }
      setIsEditing(false);
    }

    const onCancelClick = () => {
        setIsEditing(false);
    }

    const onEditClick = () => {
        setIsEditing(true);
    }

    const onDeleteClick = () => {
        setShowConfirmation(true);
    }

    const onConfirmationClose = (confirmed: boolean) => {
        if (confirmed) {
            deleteAssetAPI.request(form.id);
            onBackClick();
        }

        setShowConfirmation(false);
    };

    const onBackClick = () => {
      console.log('husky back state', state);
        navigate('/searchResults', {state: {
          filterText: state?.text ?? '', 
          paginationModel: state?.paginationModel ?? {},
          filterModel: state?.filterModel ?? {},
          sortModel: state?.sortModel ?? {},
          filterOptions: state?.filterOptions ?? {},
          coords: state?.coords ?? {},
          row: state?.row ?? {}
        }})
       
    }

    const handleChange = (fieldName: string, value: any) => {
        if(fieldName === 'ticSubject') { 
          // convert array of strings to comma separated string
          let subjects = value.join(', ');
          setForm({['ticSubject']: subjects});
        } else if(fieldName === 'ticRetentionCategory') { 
            getRetentionDisposition(value);
            setForm({['ticRetentionCategory']: value});
        } else if(fieldName === 'ticSecurityVerification') {
            let verified = value === 'Verified' ? true : false;
            setForm({['ticSecurityVerification']: value}); 
        } else if (fieldName === 'ticLocation') {
          let locations = value.join(', ');
          setForm({['ticLocation']: locations});
        } else if (fieldName === 'ticCondition') {
          let conditions = value.join(', ');
          setForm({['ticCondition']: conditions});
        } else {
          setForm({[fieldName]: value});
        }

        console.log('update form', form);
    }

    const setParkName = (value) => {
      setForm({ticParkName: value});
      console.log('update form', form);
      console.log('update parkname', value);
      let park = parks.filter((row) => row.name === value)[0];
      console.log('park', park);
      setForm({ticParkId: park.id});
      setForm({ticParkAlpha: park.alpha});
      setForm({ticParkCode: park.code});
      setForm({ticParkRegionName: park.regionName});
      setForm({ticParkPreviousName: park.previousName == null ? '' : park.previousName});
      getLocations(value);
    } 

    const onAddToDownloadsClick = () => {
      console.log('rec state', state);
      //setAddRemoveToggle(false);
      let cart = getParentDocIds(cartItems);
      cart.push(form?.ticParentDocId);
      let uniques = cart.filter((v, i, a) => a.indexOf(v) === i);
      console.log('cart', uniques);
      const url = `${environment.eticApiUrl}UpdateCart`;
      const apiService = new APIService<any[]>(axios, new CacheService, url, token);

      apiService.add(uniques, url).then((res:any[]) => {
        console.log('set cart', res);
        getCart();
      });
    }

    const recordExistsInItemsForDownload = () => {
      return cartItems.filter((row) => form?.ticParentDocId === row?.parentDocId).length ? true : false;
    }

    const onRemoveFromDownloadsClick = () => {
      let cart = getParentDocIds(cartItems);
      const filteredCart = cart.filter((row) => row !== form?.ticParentDocId);
      let uniques = filteredCart.filter((v, i, a) => a.indexOf(v) === i);
      console.log('filtered cart', uniques);
      const url = `${environment.eticApiUrl}UpdateCart`;
      const apiService = new APIService<any[]>(axios, new CacheService, url, token);

      apiService.add(filteredCart, url).then((res:any[]) => {
        console.log('set cart', res);
        getCart();
      });
     
      //setAddRemoveToggle(true);
    }

    const handleCompleteSet = () => { 
      const url = `${environment.eticApiUrl}GetParentMergedPDF/${form?.ticParentDocId}`;
      window.open(url, '_blank');
    }

    const handleRemoveClick = (rowParams) => {
      const filteredCart = cartItems.filter((row) => row.parentDocId !== rowParams.row.ticParentDocId);
      let cart = getParentDocIds(filteredCart);
      const url = `${environment.eticApiUrl}UpdateCart`;
      const apiService = new APIService<any[]>(axios, new CacheService, url, token);
  
      apiService.add(cart, url).then((res:any[]) => {
        console.log('set cart', res);
        getCart();
      });
    }

    const handleSaveClick = (items) => {
      let cart = getParentDocIds(items);

      const url = `${environment.eticApiUrl}UpdateCart`;
      const apiService = new APIService<any[]>(axios, new CacheService, url, token);

      apiService.add(cart, url).then((res:any[]) => {
        getCart();
      });

      setShowDialog(false);
    }

    const handleCloseClick = () => {
      setShowDialog(false);
      setShowUploadDialog(false);

      getTicParentChildren(form?.ticParentDocId);

    }

    const getUserAccess = () => {
      let userHasAccess = false;

      let roles: string[] = userInfo?.roles ?? [];
      roles.forEach(role => {        

        if (form?.ticDocType)
        {
          if(form.ticDocType.toLowerCase() === 'sns' && role.includes('sensitive'))
            userHasAccess = true;

          if(form.ticDocType.toLowerCase() === 'arg' && role.includes('archaeology'))
            userHasAccess = true;
        }
      });

      if (form?.ticDocType)
      {
        if(form.ticDocType.toLowerCase() === 'pub')
        userHasAccess = true;

        if(form.ticDocType.toLowerCase() === 'res' && userInfo)
          userHasAccess = true;
      }
      return userHasAccess;
    }

    const handleImageClick = (assetId) => {       
      if(!getUserAccess())
        navigate('/SecurityGuidelines');
      else {
        let imageUrl = `https://npgallery.nps.gov/GetAsset/${assetId}`;
        
        window.open(imageUrl);
      }
    }

    const getParentDocIds = (items) => {
      let parentDocIds = [];
      items.forEach(item => {
        parentDocIds.push(item.parentDocId);
      });
  
      return parentDocIds;
    }

    const getCart = () => { 
      const url = `${environment.eticApiUrl}GetCart`;
      const apiService = new APIService<Cart>(axios, new CacheService, url, token);
  
      apiService.getAnyAsync().then((res:any[]) => {
        SetCartState(res);
        let exists = res.filter((row) => form?.ticParentDocId === row?.parentDocId).length ? true : false;
        console.log('exists', exists);
        setAddRemoveToggle(exists);
      });
    }
    
    const handleDownloadItemsClick = (items) => {
      const url = `${environment.eticApiUrl}CreateOrderFromCart`;
      const apiService = new APIService<Cart>(axios, new CacheService, url, token);

      apiService.getAnyAsync().then((res:any) => {
        console.log('cart  created', res);
        getCart();

        const orderUrl = `${environment.eticApiUrl}CreateOrderZip/${res.id}`;
        const orderApiService = new APIService<Cart>(axios, new CacheService, orderUrl, token);
    
        orderApiService.getAnyAsync().then((res:any) => {
          console.log('zip  creating', res);
          navigate('/orderHistory');

        }).catch(err => { 
          navigate('/orderHistory');
        });
      });
    
      setShowDialog(false);
    }

    const handleGenerateTicNumClick = async () => {
      const url = `${environment.npgAPI}etic/generateticnumber?checkconflict`;
      const apiService = new APIService<any>(axios, new CacheService, url, token);
     
      const { title, ticParkId: parkId, ticInfoType: infoType } = form;
      const payload = { title, parkId, infoType };

      //Validate before calling API
      if (!title || !parkId || !infoType) {
        alert("Title, Park, and Info Type are all required to generate a TIC number.");
        return;
      }

      apiService.add(payload).then((res:any) => {
        console.log('tic',res);
        setForm({['ticNumber']: res?.assignedTicNumber?.ticNumber ? res?.assignedTicNumber?.ticNumber : res.existingTicNumber.ticNumber});
      });  
    }

    const handleViewDownloadsClick = () => {
      setShowDialog(true);
    }

    const handleUpload = () => { 
      setShowUploadDialog(true);
    }

    const formatDate = (date) => { 
      let d = new Date(date);
      
      return d.toLocaleDateString();
    }

    const deleteAllChildren = () => {
      setChildren([]);
    }

    function unique(array, propertyName) {
      return array && array.filter((e, i) => array.findIndex(a => a[propertyName] === e[propertyName]) === i);
    }
  
    const formatFileSize = (bytes) => {
      const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
      
      // If the size is 0, return "0 Bytes"
      if (bytes === 0) return '0 Bytes';
      
      // Calculate the power of 1024 based on the size
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      
      // Limit to 2 decimal places and return formatted size with the appropriate unit
      return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + units[i];
    }

    /* SUBCOMPONENTS */
    
    function SaveButton() {

      const isDisabled =
        isSaving ||
        !form?.ticNumber ||
        !form?.ticRetentionCategory ||
        !form?.ticRetentionDisposition ||
        !form?.ticParkName ||
        !form?.ticUniformTitleCode ||
        !form?.ticInfoType ||
        !form?.ticDocType ||
        !form?.title;

      const title = isDisabled
        ? "When all required fields are filled out, this button will be enabled."
        : "";

      return (
        <Tooltip title={title} arrow>
          <span>
            <Button
              id="save"
              variant="outlined"
              className="margin-right"
              startIcon={<FontAwesomeIcon icon={["fas", "floppy-disk-circle-arrow-right"]} />}
              disabled={isDisabled}
              onClick={onSaveClick}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      );
    }

    function EditButton() {

      return (
        <Button
          id="edit"
          variant="outlined"
          className="margin-right"
          startIcon={<FontAwesomeIcon icon={["fas", "pen"]}/>}
          onClick={() => {
              onEditClick();
          }}>
          Edit
      </Button>
      );
    }

    function CancelButton() {

      if(form?.id){
        return (
          <Button
            id="cancel"
            variant="outlined"
            className="margin-right"
            startIcon={<FontAwesomeIcon icon={["fas", "circle-x"]}/>}
            onClick={() => {
                onCancelClick();
            }}>
            Cancel
        </Button>
        );
      }else{
        return null;
      }

    }

    function DeleteButton() {

      if(form?.id){
        return (
          <Button
            id="delete"
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={["fas", "trash-can"]}/>}
            onClick={() => {
                onDeleteClick();
            }}>
            Delete
          </Button>
        );
      }else{
        return null;
      }
    }

    function EditFilesButton() {

      const isDisabled = !form?.id && !form.ticParentDocId;

      const title = isDisabled
        ? "When all required fields are filled out, and the record is saved, this button will be enabled."
        : "";

      return (
        <Tooltip title={title} arrow>
          <span>
            <Button
              id="completeSet"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={["fas", "upload"]}/>}
              disabled={isDisabled}
              onClick={handleUpload}
              sx={{width: 140}}
            >
              Edit File(s)
            </Button>
          </span>
        </Tooltip>
      );
    }

    function EditSaveToolbar( { showEditFilesButton = true } ) {
      return (
        <Stack direction="row" spacing={2}>
        {
            isEditing && isAdmin
            ?
            <>
                <SaveButton />
                <CancelButton />
                { showEditFilesButton && <EditFilesButton /> }
            </>
            :
            isAdmin &&
            <>
              <EditButton />
              { showEditFilesButton && <EditFilesButton /> }
            </>
        }

        { isAdmin &&
          <>
            <DeleteButton />
          </>                    
        } 
      </Stack>  
      );
    }


    return (
        <Card sx={{minWidth: 275, paddingBottom: 20, backgroundColor: '#eae9e6'}}>
            {showConfirmation &&
                <ConfirmationDialog
                    title={"Delete Asset"}
                    dialogText={"Are you sure you want to delete this asset?"}
                    closeButtonText={'No'}
                    handleActionButtonText={'Yes'}
                    onChangeEvent={onConfirmationClose}/>
            }

            {showUploadDialog &&
                <UploadDialog
                  record={form}
                  handleSaveFileEdit={handleSaveFileEdit}
                  handleCloseClick={handleCloseClick}
                />
            }

            {!environment.publicMode && showDialog &&
              <ViewDownloadsDialog
                title={"Selected Items For Download"}
                //downloads={itemsForDownload}
                closeButtonText={'Cancel'}
                handleActionButtonText={'Download'}
                //handleRemoveItem={handleRemoveClick}
                //handleSaveClick={handleSaveClick}
                handleDownloadItemsClick={handleDownloadItemsClick}
                handleCloseClick={handleCloseClick}
              />
            }
                
            <CardContent className="record-form">              
              { 
                ( pageId ?              
                  <div className="center">
                    <Button
                      id="back"
                      variant="outlined"
                      className="margin-bottom margin-right"
                      startIcon={<FontAwesomeIcon icon={["fas", "arrow-left"]}/>}
                      onClick={() => {
                          onBackClick();
                      }}>
                      Back
                    </Button>

                    {!environment.publicMode && (
                    <Stack direction="row" spacing={2}>
                      <>
                        <Button 
                          id="upload"
                          variant="outlined" 
                          className=""         
                          startIcon={<FontAwesomeIcon icon={["fas", "folder-arrow-down"]} />}
                          onClick={handleViewDownloadsClick}>
                            Items Selected For Download ({cartItems.length})
                        </Button>

                        { addRemoveToggle  ?
                          <Button
                            id="download"
                            variant="outlined"
                            className="margin-left"
                            startIcon={<FontAwesomeIcon icon={["fas", "plus-square"]}/>}
                            onClick={() => {
                                onAddToDownloadsClick();
                            }}>
                            Add to items selected for download
                          </Button>
                          :
                          <Button
                            id="remove"
                            variant="outlined"
                            className="margin-left"
                            startIcon={<FontAwesomeIcon icon={["fas", "minus-square"]}/>}
                            onClick={() => {
                                onRemoveFromDownloadsClick();
                            }}>
                            Remove from items selected for download
                          </Button>
                        }  
                      </> 
                    </Stack>
                    )}
                  </div>
                : null)
              }
              
              <div className="center">

                <Typography variant="h5" component="div" sx={{ marginTop: 3, marginBottom: 2 }}>
                    {form?.title}
                </Typography>
                <Helmet>
                    <meta charSet="utf-8"/>
                    <title>Record View</title>
                </Helmet>
                <EditSaveToolbar showEditFilesButton={ false }/>  

              </div>
                <br></br>

                <h3 className="center">
                  <b>
                  {
                    ( 
                      pageId ?
                      'Record View':
                      'Create New Record'
                    )
                  }
                  </b>
                </h3>
                <form onSubmit={handleSubmit} aria-disabled>

                  <Stack className="center" direction="column" spacing={1}>
                    <WhiteOutlinedTextField
                      id="ticNumber"
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="TIC Number"
                      required
                      onChange={e => handleChange('ticNumber', e.target.value)}
                      value={form?.ticNumber || ''}
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      InputProps={{
                        endAdornment: !form?.ticNumber && (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="generate tic num"
                              onClick={handleGenerateTicNumClick}
                              edge="end"
                            >
                              <FontAwesomeIcon color="#1976d2" icon={["fas", "plus"]} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <WhiteOutlinedTextField 
                      id="title"
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Title"
                      required
                      onChange={e => handleChange('title', e.target.value)}
                      value={form?.title || ''}
                      multiline
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />

                    <WhiteOutlinedTextField 
                        id="parkName"
                        type="text"
                        variant='outlined'
                        color='secondary'
                        label="Park Name"
                        onChange={e => setParkName(e.target.value)}
                        value={form?.ticParkName || ''}
                        disabled={!isEditing}
                        required
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                        fullWidth
                        select
                      >
                        {parks.map((option) => (
                          <MenuItem key={option.id} value={option.name}>
                            {option.name}
                          </MenuItem>
                        ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                        id="parkAlpha"
                        type="text"
                        variant='outlined'
                        color='secondary'
                        label="Park Alpha"
                        value={form?.ticParkAlpha || ''}       
                        disabled={true}
                        fullWidth
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}  
                    />

                    <WhiteOutlinedTextField 
                        id="parkCode"
                        type="text"
                        variant='outlined'
                        color='secondary'
                        label="Park Code"
                        value={form?.ticParkCode || ''}                        
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                        fullWidth
                        disabled={true}
                    />

                    <WhiteOutlinedTextField 
                        id="prevParkName"
                        type="text"
                        variant='outlined'
                        color='secondary'
                        label="Park Previous Name"
                        value={form?.ticParkPreviousName || ''}                        
                        disabled={true}
                        fullWidth
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Region Name"
                      value={form?.ticParkRegionName || ''}                   
                      disabled={true}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />          

                    <hr className="solid"></hr>                  
                      
                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Info Type"
                      required
                      onChange={e => handleChange('ticInfoType', e.target.value)}
                      value={form?.ticInfoType || ''}                         
                      disabled={!isEditing}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}  
                      fullWidth    
                      select>{infoTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                        type="text"
                        variant='outlined'
                        color='primary'
                        label="Revision Code"
                        onChange={e => handleChange('ticRevisionCode', e.target.value)}
                        value={form?.ticRevisionCode || ''}
                        disabled={!isEditing}
                        fullWidth
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}    
                    />

                    <WhiteOutlinedTextField 
                        type="text"
                        variant='outlined'
                        color='primary'
                        label="Duplication Code"
                        onChange={e => handleChange('ticDupeCode', e.target.value)}
                        value={form?.ticDupeCode || ''}
                        disabled={!isEditing}
                        fullWidth
                        InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Security Type"
                      required
                      onChange={e => handleChange('ticDocType', e.target.value)}
                      value={form?.ticDocType || ''}                     
                      disabled={!isEditing}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      select>
                        {securityTypes.map((option) => (
                           <MenuItem key={option.label} value={option.value}>
                              {option.label}
                            </MenuItem>
                        ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                        type="text"
                        variant='outlined'
                        color='secondary'
                        label="Security Verification"
                        onChange={e => handleChange('ticSecurityVerification', e.target.value == 'Verified' ? true : false)}
                        value={form.hasOwnProperty('ticSecurityVerification') ? form.ticSecurityVerification == true ? 'Verified' : 'Needs Verification' : ''}  
                        disabled={!isEditing}
                        InputLabelProps={
                        {
                          style: 
                          {
                            fontWeight: 'bold',
                            background: 'white',
                            paddingLeft: '3px',
                            paddingRight: '3px'
                          }
                        }}
                        fullWidth
                        select
                      >
                        {securityVerifications.map((option) => (
                          <MenuItem key={option.label} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </WhiteOutlinedTextField>

                    <hr className="solid"></hr>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Project Name"
                      onChange={e => handleChange('ticProject', e.target.value)}
                      value={form?.ticProject || ''}
                      disabled={!isEditing}
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}                      
                      fullWidth
                      inputProps={{maxLength: 300}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="PMIS #"
                      disabled={!isEditing}
                      onChange={e => handleChange('ticPmisNumber', e.target.value)}
                      value={form?.ticPmisNumber || form?.pmisNumber || ''}
                      inputProps={{maxLength: 6}}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      onInput={(e) => onlyNumbers(e) }
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="PMIS Component"
                      onChange={e => handleChange('ticPmisComponent', e.target.value)}
                      value={form?.ticPmisComponent || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 3}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Alternate PMIS #'s"
                      onChange={e => handleChange('ticAlternatePmis', e.target.value)}
                      value={form?.ticAlternatePmis || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 100}}
                    />

                    <hr className="solid"></hr>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Package"
                      onChange={e => handleChange('ticPackage', e.target.value)}
                      value={form?.ticPackage || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 5}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Alternate TIC ID #'s"
                      onChange={e => handleChange('ticAlternateTicId', e.target.value)}
                      value={form?.ticAlternateTicId || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 100}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Contract #"
                      onChange={e => handleChange('ticContract', e.target.value)}
                      value={form?.ticContract || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 100}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Author"
                      onChange={e => handleChange('ticAuthor', e.target.value)}
                      value={form?.ticAuthor || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 200}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Corporate Author"
                      onChange={e => handleChange('ticCorpAuthor', e.target.value)}
                      value={form?.ticCorpAuthor || ''}
                      fullWidth
                      multiline
                      disabled={!isEditing}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}   
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Uniform Title Code"
                      onChange={e => handleChange('ticUniformTitleCode', e.target.value)}
                      value={form?.ticUniformTitleCode || ''}
                      fullWidth
                      multiline
                      disabled={!isEditing}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      required
                      select>
                      {uniformTitle.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>
                    
                    <hr className="solid"></hr>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <WhiteOutlinedDateField 
                        label="Publish Date"                          
                        value={( form.ticPublishDate? dayjs(form.ticPublishDate) : null )}
                        disabled={!isEditing}
                        onChange={e => handleChange('ticPublishDate', e)}  
                        className="datePicker"
                        slotProps={
                          {
                            textField: {
                              InputLabelProps:{
                                style: 
                                {
                                  fontWeight: 'bold',
                                  background: 'white',
                                  paddingLeft: '3px',
                                  paddingRight: '3px'
                                }
                              }
                          }}}
                        />
                    </LocalizationProvider>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Date Range"
                      onChange={e => handleChange('ticInfoDate', e.target.value)}
                      value={form?.ticInfoDate}                       
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />
                    
                    <FormControlLabel 
                      control={
                        <Checkbox 
                          onChange={e => handleChange('ticEstimatedDate', e.target.checked)} 
                          checked={form?.ticEstimatedDate || false}
                          disabled={!isEditing}
                        />
                      } 
                      label={
                        <Typography fontWeight="bold">
                          Estimated Date
                        </Typography>
                      }
                    />

                    <hr className="solid"></hr>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Retention Category"
                      required
                      onChange={e => handleChange('ticRetentionCategory', e.target.value)}
                      value={form?.ticRetentionCategory || ''}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      disabled={!isEditing}
                      fullWidth
                      select>
                        {retentionCategory.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option} 
                          </MenuItem>
                        ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Retention Disposition"
                      required
                      onChange={e => handleChange('ticRetentionDisposition', e.target.value)}
                      value={form?.ticRetentionDisposition || ''}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      disabled={!isEditing}
                      fullWidth
                      select>
                      {retentionDisposition.map((option) => (
                      <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Location of Original"
                      onChange={e => handleChange('ticStorage', e.target.value)}
                      value={form?.ticStorage || ''}
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Storage"
                      onChange={e => handleChange('ticStorage', e.target.value)}
                      value={form?.ticStorage || ''} 
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth                    
                      select
                    >
                      {storage.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="FRC Accession Number"
                      onChange={e => handleChange('ticFrcAccessionNumber', e.target.value)}
                      value={form?.ticFrcAccessionNumber || ''}
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                    />

                    <FormControlLabel 
                      control={
                        <Checkbox 
                          onChange={e => handleChange('ticElectronicTransfer', e.target.checked)} 
                          checked={form?.ticElectronicTransfer || false}
                          disabled={!isEditing}
                        />
                      } 
                      label={
                        <Typography fontWeight="bold">
                          Electronic Transfer
                        </Typography>
                      }
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="FRC Box"
                      onChange={e => handleChange('ticFrcBox', e.target.value)}
                      value={form?.ticFrcBox || ''}
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }} 
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Folder"
                      onChange={e => handleChange('ticFolder', e.target.value)}
                      value={form?.ticFolder || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 3}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="NARA Ascension Number"
                      onChange={e => handleChange('ticNaraAccessionNumber', e.target.value)}
                      value={form?.ticNaraAccessionNumber || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 50}}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='primary'
                      label="Sheet Count"
                      onChange={e => handleChange('ticPhysicalSheetCount', e.target.value)}
                      value={pageCount || 0}
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }} 
                    />

                    <FormControlLabel 
                      control={
                        <Checkbox 
                          onChange={e => handleChange('ticOriginalMissing', e.target.checked)} 
                          checked={form?.ticOriginalMissing || false}
                          disabled={!isEditing}
                        />
                      } 
                      label={
                        <Typography fontWeight="bold">
                          Original Missing
                        </Typography>
                      } 
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Rights Code"
                      onChange={e => handleChange('ticRights', e.target.value)}
                      value={form?.ticRights || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      select>
                      {rights.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="NPS Source"
                      onChange={e => handleChange('ticNpsSource', e.target.value)}
                      value={form?.ticNpsSource || ''}
                      disabled={!isEditing}                      
                      InputLabelProps={
                      {
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      fullWidth
                      inputProps={{maxLength: 50}}
                    />

                    <hr className="solid"></hr>

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='primary'
                      label="Comments"
                      onChange={e => handleChange('ticComment', e.target.value)}
                      value={form?.ticComment || ''}
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      multiline
                      fullWidth
                      disabled={!isEditing}
                    />

                    <hr className="solid"></hr>

                     <Autocomplete
                        multiple
                        id="checkboxes-tags-subjects"
                        options={subjects}
                        value={(form?.ticSubject && form.ticSubject.trim()) ? form.ticSubject.split(',').map(item => item.trim()).filter(item => item.length > 0) : []}
                        disableCloseOnSelect
                        disabled={!isEditing}
                        onChange={(e, newValue) => handleChange('ticSubject', newValue)}
                        getOptionLabel={(option) => option}
                        fullWidth
                        renderOption={(props, option, { selected }) => {
                          const { ...optionProps } = props;
                          return (
                            <li key={option} {...optionProps}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}                                  
                              />
                              {option}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <WhiteOutlinedTextField  {...params} label="Subjects"                       
                            InputLabelProps={
                            {
                              style: 
                              {
                                fontWeight: 'bold',
                                background: 'white',
                                paddingLeft: '3px',
                                paddingRight: '3px'
                              }
                            }} 
                          />
                        )}
                        ChipProps={{
                        // style each Chip
                          sx: {
                            // when the Autocomplete is disabled, chips get .Mui-disabled
                            "&.MuiChip-root&.Mui-disabled": {
                              // bump up contrast
                              backgroundColor: 'rgba(0, 0, 0, 0.08)', 
                              color: 'rgba(0, 0, 0, 0.87)',
                              opacity: 1
                            },
                            // keep the little “×” icon grayed
                            "&.Mui-disabled&.MuiChip-deleteIcon": {
                              color: (theme) => theme.palette.action.disabled,
                              opacity: 0.08,
                            },
                          },
                        }}
                      />

                    <Autocomplete
                      multiple
                      id="checkboxes-tags-locations"
                      options={locations}
                      value={(form?.ticLocation && form.ticLocation.trim()) ? form.ticLocation.split(',').map(item => item.trim()).filter(item => item.length > 0) : []}
                      disableCloseOnSelect
                      disabled={!isEditing}
                      onChange={(e, newValue) => handleChange('ticLocation', newValue)}
                      getOptionLabel={(option) => option}
                      renderOption={(props, option, { selected }) => {
                        const { ...optionProps } = props;
                        return (
                          <li key={option} {...optionProps}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}                              
                            />
                            {option}
                          </li>
                        );
                      }}
                      style={{ width: 600 }}
                      renderInput={(params) => (
                        <WhiteOutlinedTextField  {...params} label="Location" placeholder="Locations"                      
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                        />
                      )}
                      ChipProps={{
                        // style each Chip
                          sx: {
                            // when the Autocomplete is disabled, chips get .Mui-disabled
                            "&.MuiChip-root&.Mui-disabled": {
                              // bump up contrast
                              backgroundColor: 'rgba(0, 0, 0, 0.08)', 
                              color: 'rgba(0, 0, 0, 0.87)',
                              opacity: 1
                            },
                            // keep the little “×” icon grayed
                            "&.Mui-disabled&.MuiChip-deleteIcon": {
                              backgroundColor: (theme) => theme.palette.action.disabledBackground,
                              color: (theme) => theme.palette.action.disabled,
                              opacity: (theme) => theme.palette.action.disabledOpacity,
                            },
                          },
                        }}
                    />

                    <WhiteOutlinedTextField 
                      type="text"
                      variant='outlined'
                      color='secondary'
                      label="Media Type"
                      onChange={e => handleChange('ticMedia', e.target.value)}
                      value={form?.ticMedia || ''}                          
                      disabled={!isEditing}
                      fullWidth
                      InputLabelProps={{ 
                        style: 
                        {
                          fontWeight: 'bold',
                          background: 'white',
                          paddingLeft: '3px',
                          paddingRight: '3px'
                        }
                      }}
                      select>
                      {mediaType.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </WhiteOutlinedTextField>

                    <Autocomplete
                      multiple
                      id="checkboxes-tags-condition"
                      options={condition}
                      value={
                        (form?.ticCondition && form.ticCondition.trim()) 
                          ? form.ticCondition.split(',').map(item => item.trim()).filter(item => item.length > 0) 
                          : []}
                      disableCloseOnSelect
                      onChange={(e, newValue) => handleChange('ticCondition', newValue)}
                      getOptionLabel={(option) => option}
                      fullWidth
                      renderOption={(props, option, { selected }) => {
                        const { ...optionProps } = props;
                        return (
                          <li key={option} {...optionProps}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}                                  
                            />
                            {option}
                          </li>
                        );
                      }}
                      disabled={!isEditing}
                      renderInput={(params) => (
                        <WhiteOutlinedTextField  {...params} label="Condition" placeholder="Conditions"                       
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                        />
                      )}
                    />
                     
                     <FormControlLabel 
                      control={
                        <Checkbox 
                          onChange={e => handleChange('ticMicrofilmMissing', e.target.checked)} 
                          checked={form?.ticMicrofilmMissing || false}
                          disabled={!isEditing}
                        />
                      } 
                      label={
                        <Typography fontWeight="bold">
                          Microfilm Missing
                        </Typography>
                      } 
                    />

                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <WhiteOutlinedDateField 
                          label="Microfilmed Date"
                          value={( form.ticMicrofilmedDate? dayjs(form.ticMicrofilmedDate) : null )}
                          onChange={e => handleChange('ticMicrofilmedDate', e)}
                          disabled={!isEditing}
                          className="datePicker"                                                
                          slotProps={
                          {
                            textField: {
                              InputLabelProps:{
                                style: 
                                {
                                  fontWeight: 'bold',
                                  background: 'white',
                                  paddingLeft: '3px',
                                  paddingRight: '3px'
                                }
                              }
                          }}}
                         />
                      </LocalizationProvider>

                      <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Microfilm Roll Number"
                          onChange={e => handleChange('ticMicrofilmRollNumber', e.target.value)}
                          value={form?.ticMicrofilmRollNumber || ''}
                          disabled={!isEditing}                      
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}    
                          fullWidth                     
                          inputProps={{maxLength: 20}}
                      />

                      <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Scale"
                          onChange={e => handleChange('ticScale', e.target.value)}
                          value={form?.ticScale || ''}
                          disabled={!isEditing}                        
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                          fullWidth                       
                          inputProps={{maxLength: 20}}
                      />

                      <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Size"
                          onChange={e => handleChange('ticSize', e.target.value)}
                          value={form?.ticSize || ''}
                          disabled={!isEditing}                          
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                          fullWidth                       
                          select>
                          {size.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                      </WhiteOutlinedTextField>

                      <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="URL"
                          onChange={e => handleChange('ticUrl', e.target.value)}
                          value={form?.ticUrl || ''}
                          disabled={!isEditing}                       
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }} 
                          fullWidth                         
                          inputProps={{maxLength: 50}}
                      />

                    { form?.ticInfoType === "F-TIC Aerial" &&
                      <>
                        <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Aerial Frames"
                          onChange={e => handleChange('ticAerialFrames', e.target.value)}
                          value={form?.ticAerialFrames || ''}
                          fullWidth                          
                          disabled={!isEditing}                      
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                          inputProps={{maxLength: 300}}
                        />

                        <FormControlLabel 
                          control={
                            <Checkbox value={form?.ticAerialIndex} />
                          } 
                          label={
                            <Typography fontWeight="bold">
                              Aerial Index
                            </Typography>
                          }  
                        />

                        <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Can Number"
                          onChange={e => handleChange('ticAericalCanNumber', e.target.value)}
                          value={form?.ticAericalCanNumber || ''}
                          fullWidth                          
                          disabled={!isEditing}                      
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                          inputProps={{maxLength: 300}}
                        />

                        <FormControlLabel 
                          control={
                            <Checkbox value={form?.ticAerialControls} />
                            } 
                          label={
                            <Typography fontWeight="bold">
                              Controls
                            </Typography>
                          }
                        />

                        <WhiteOutlinedTextField 
                          type="text"
                          variant='outlined'
                          color='secondary'
                          label="Flight ID"
                          onChange={e => handleChange('ticAerialFlightId', e.target.value)}
                          value={form?.ticAerialFlightId || ''}
                          fullWidth                          
                          disabled={!isEditing}                      
                          InputLabelProps={
                          {
                            style: 
                            {
                              fontWeight: 'bold',
                              background: 'white',
                              paddingLeft: '3px',
                              paddingRight: '3px'
                            }
                          }}
                          inputProps={{maxLength: 300}}
                        />
                      </>
                    }

                    <EditSaveToolbar showEditFilesButton={ false } />

                    { loading && <CircularProgress />}

                      <>
                        { form?.ticPdfMergeLocation &&
                          <Button
                            id="set"
                            variant="contained"
                            className="third"
                            startIcon={<FontAwesomeIcon icon={["fas", "download"]}/>}
                            onClick={handleCompleteSet}>
                            Complete Set
                          </Button>
                        }
                        { isEditing || isAdmin ? 
                        <>
                          <br/>
                          <Typography variant="h5">
                            Documents in set ({children?.length || 0})
                          </Typography>
                          <EditFilesButton />
                          <div className="center" style={{ height: 420, width: '100%' }}>
                            <DataGrid
                              getRowHeight={(params) => 'auto'}
                              sx={{ '.MuiDataGrid-columnHeaderTitle': {fontWeight: 'bolder'},
                              '.MuiDataGrid-columnHeaderTitleContainer': {borderBottom: 1 },
                              '.MuiDataGrid-row': {cursor: 'pointer', borderBottom: 1},
                              '.MuiDataGrid-iconButtonContainer': {visibility: 'visible' },
                              '.MuiDataGrid-sortIcon': {opacity: 'inherit !important' }
                              }}
                              rows={children.map((result, index) => (
                                {
                                  id: index,
                                  title: result?.title,
                                  pdfName: result?.pdfName,
                                  
                                }
                              )) || []}
                              columns={columns}
                              initialState={{
                                pagination: {
                                  paginationModel: { page: 0, pageSize: 5 },
                                },
                              }}
                              pageSizeOptions={[5, 10, 25, 50]}
                            />
                          </div>
                          {
                            !children?.length &&
                              <Typography variant="h6" className="answer">
                                This document has not been scanned, please <Link to={"/teams"}>contact TIC</Link> for assistance.
                              </Typography>    
                          }
                        </>
                      : <></>}
                      { children?.length && pageId ?
                        <>
                          <ImageList sx={{ width: 800 }}>
                            {children.map((item) => (
                              <ImageListItem key={item.img}>
                                <img
                                  src={`https://npgallery.nps.gov/GetAsset/${item.AssetID}/thumbsmall`}
                                  alt={item.title}
                                  loading="lazy"
                                  style={{cursor: 'pointer', minHeight: '100px', minWidth: '100px'}}
                                  onClick={(event: React.MouseEvent<HTMLImageElement>) => handleImageClick(item.AssetID)}
                                />
                                <ImageListItemBar
                                  title={item.title}
                                  subtitle={`Size: ${formatFileSize(item.pdfSize)}`}
                                  actionIcon={
                                    <IconButton
                                    onClick={() => handleImageClick(item.AssetID)}
                                    sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                      aria-label={`View ${item.title}`}
                                    >
                                      <FontAwesomeIcon icon={["fas", "info-circle"]}  />
                                    </IconButton>
                                  }
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </>
                        : 
                          (!getUserAccess()) ? 
                          <h2 className="answer">The documents related to this record are currently restricted, for more information please view our page on <Link to={"/SecurityGuidelines"}>Security Guidelines.</Link></h2>
                          :  
                          <h2 className="answer">This document has not been scanned, please <Link to={"./../chat"}>chat with
                          TIC</Link> for assistance</h2>
                      }
                      </>  
                    </Stack>
                  </form>
                  
                  <div className="helper center">
                    <p className="answer"><br/>For information on Accessibility and 508 Contact Information <Link
                        to={"/Accessibility"}>view accessibility information</Link>.
                    </p>
                  </div>
            </CardContent>

        </Card>
    );
}

export default Record;